import { useMemo } from 'react';
import {
  type AnyChainflipAsset,
  type ChainflipAsset,
  getInternalAsset,
} from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import { calculateTotalEffectiveBorrowableAmount } from '@chainflip/utils/lending';
import { isLiquidityProvisionApp } from '@/shared/featureFlags';
import { type GetAllLendingPoolsQuery } from '@/shared/graphql/generated/cache/graphql';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { useLendingConfig } from '@/shared/hooks/useLendingConfig';
import { useOraclePrices } from '@/shared/hooks/useOraclePrice';
import useRpcRequest from '@/shared/hooks/useRpcRequest';
import { compareAssets } from '@/shared/utils/chainflip';
import {
  type ApyByPeriod,
  DEFAULT_APY_PERIOD,
  EMPTY_APY_BY_PERIOD,
  LENDING_POOLS_APY_QUERY,
  LENDING_POOLS_QUERY,
} from '@/shared/utils/lending';
import { pipsToPercent } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

type PoolBalance = {
  totalAmount: TokenAmount;
  totalAvailableAmount: TokenAmount;
  totalBorrowedAmount: TokenAmount;
  totalBorrowableAmount: TokenAmount;
};

export const useLendingPools = (variables?: { asset?: ChainflipAsset }) => {
  const prices = useOraclePrices();
  const { interestFeeNetworkShareBps, isLoading: isLendingConfigLoading } = useLendingConfig();

  // RPC data source (used for liquidity-provision app)
  const rpcResult = useRpcRequest({
    method: 'cf_lending_pools',
    args: [],
    refetchInterval: 6_000,
    enabled: isLiquidityProvisionApp,
  });

  // GraphQL cache data source (used for explorer)
  const { data: cacheData, isLoading: isCacheLoading } = useGqlQuery(LENDING_POOLS_QUERY, {
    context: { clientName: 'statechainCache' },
    variables,
    refetchInterval: 6_000,
    enabled: !isLiquidityProvisionApp,
    select: (d: GetAllLendingPoolsQuery) =>
      d.lendingPools?.nodes.toSorted((a, b) => compareAssets(a.asset, b.asset)),
  });

  const rpcData = useMemo(() => {
    if (!isLiquidityProvisionApp || !rpcResult?.data) return undefined;

    return rpcResult.data
      .map((pool) => {
        const chainflipAsset = getInternalAsset(pool.asset);

        if (variables?.asset && variables.asset !== chainflipAsset) {
          return null;
        }

        const usdPrice = prices?.[chainflipAsset]?.usdPrice ?? 0;
        const totalAmount = TokenAmount.fromAsset(pool.total_amount, chainflipAsset);
        const totalAvailableAmount = TokenAmount.fromAsset(pool.available_amount, chainflipAsset);
        const totalBorrowedAmount = totalAmount.sub(totalAvailableAmount).max(TokenAmount.ZERO);

        return {
          asset: chainflipAsset,
          totalAmount: pool.total_amount.toString(),
          totalAvailableAmount: pool.available_amount.toString(),
          totalBorrowedAmount: totalBorrowedAmount.toString(),
          totalAmountUsd: totalAmount.toUsdNumber(usdPrice).toString(),
          totalAvailableAmountUsd: totalAvailableAmount.toUsdNumber(usdPrice).toString(),
          totalBorrowedAmountUsd: totalBorrowedAmount.toUsdNumber(usdPrice).toString(),
          interestRateBps: Math.trunc(pool.current_interest_rate / 100),
          utilisationRateBps: Math.trunc(pool.utilisation_rate / 100),
          utilisationCapBps: pool.utilisation_cap ? Math.trunc(pool.utilisation_cap / 100) : 10_000,
          originationFeeBps: Math.trunc(pool.origination_fee / 100),
          liquidationFeeBps: Math.trunc(pool.liquidation_fee / 100),
          interestAtZeroUtilisationBps: Math.trunc(
            pool.interest_rate_curve.interest_at_zero_utilisation / 100,
          ),
          junctionUtilisationBps: Math.trunc(pool.interest_rate_curve.junction_utilisation / 100),
          interestAtJunctionBps: Math.trunc(
            pool.interest_rate_curve.interest_at_junction_utilisation / 100,
          ),
          interestAtMaxUtilisationBps: Math.trunc(
            pool.interest_rate_curve.interest_at_max_utilisation / 100,
          ),
        };
      })
      .filter(isNotNullish)
      .toSorted((a, b) => compareAssets(a.asset, b.asset));
  }, [rpcResult?.data, prices, variables?.asset]);

  const data = isLiquidityProvisionApp ? rpcData : cacheData;

  const { data: apyData, isLoading: isApyDataLoading } = useGqlQuery(LENDING_POOLS_APY_QUERY, {
    context: { clientName: 'lpProcessor' },
    variables,
    refetchInterval: 30_000,
  });

  const apyByAsset: Map<AnyChainflipAsset, { supply: ApyByPeriod; borrow: ApyByPeriod }> =
    useMemo(() => {
      const map = new Map<AnyChainflipAsset, { supply: ApyByPeriod; borrow: ApyByPeriod }>();

      if (!apyData?.lendingPools?.nodes) return map;

      for (const item of apyData.lendingPools.nodes) {
        const firstStat = item.stats.nodes?.[0];
        if (firstStat?.asset) {
          map.set(firstStat.asset, {
            supply: {
              '6H': Number(firstStat.projectedApy6H ?? 0),
              '24H': Number(firstStat.projectedApy24H ?? 0),
              '72H': Number(firstStat.projectedApy72H ?? 0),
              '168H': Number(firstStat.projectedApy168H ?? 0),
              '720H': Number(firstStat.projectedApy720H ?? 0),
            },
            borrow: {
              '6H': Number(firstStat.projectedBorrowApy6H ?? 0),
              '24H': Number(firstStat.projectedBorrowApy24H ?? 0),
              '72H': Number(firstStat.projectedBorrowApy72H ?? 0),
              '168H': Number(firstStat.projectedBorrowApy168H ?? 0),
              '720H': Number(firstStat.projectedBorrowApy720H ?? 0),
            },
          });
        }
      }
      return map;
    }, [apyData]);

  const lendingPools = useMemo(() => {
    if (!data) return [];

    return data.map((pool) => {
      const poolHasLiquidity = TokenAmount.fromAsset(pool.totalAmount, pool.asset).gt(0);
      const interestRateBps = poolHasLiquidity
        ? pool.interestRateBps
        : pool.interestAtZeroUtilisationBps + interestFeeNetworkShareBps;
      const poolApy = apyByAsset.get(pool.asset);
      const projectedSupplyApyByPeriod = poolApy?.supply ?? EMPTY_APY_BY_PERIOD;
      const projectedBorrowApyByPeriod = {
        CURRENT: pipsToPercent(interestRateBps),
        ...(poolApy?.borrow ?? EMPTY_APY_BY_PERIOD),
      };
      return {
        ...pool,
        // Temporarily worked around the incorrect IR curve values by adding interestFeeNetworkShareBps on top of the values exposed by the RPC.
        // To be removed after protocol incorporates this network fee in the exposed RPC values.
        interestAtZeroUtilisationBps:
          pool.interestAtZeroUtilisationBps + interestFeeNetworkShareBps,
        junctionUtilisationBps: pool.junctionUtilisationBps + interestFeeNetworkShareBps,
        interestAtJunctionBps: pool.interestAtJunctionBps + interestFeeNetworkShareBps,
        interestAtMaxUtilisationBps: pool.interestAtMaxUtilisationBps + interestFeeNetworkShareBps,
        defaultProjectedSupplyApy: projectedSupplyApyByPeriod[DEFAULT_APY_PERIOD.value],
        projectedSupplyApyByPeriod,
        defaultProjectedBorrowApy: projectedBorrowApyByPeriod[DEFAULT_APY_PERIOD.value],
        projectedBorrowApyByPeriod,
        // the projected APYs above fall back to 0 when the APY query fails or has no stats for this asset
        hasProjectedApy: poolApy !== undefined,
        utilisationRateBps: poolHasLiquidity ? pool.utilisationRateBps : 0,
        interestRateBps,
      };
    });
  }, [data, apyByAsset, interestFeeNetworkShareBps]);

  const maxApy = lendingPools.reduce(
    (max, pool) => (pool.defaultProjectedSupplyApy > max ? pool.defaultProjectedSupplyApy : max),
    0,
  );

  const transformedData = useMemo(
    () => ({
      lendingPools,
      poolAssets: data?.map((pool) => pool.asset) ?? [],
      poolBalances: (data ?? []).reduce(
        (acc, balance) => {
          const totalAmount = TokenAmount.fromAsset(balance.totalAmount, balance.asset);
          const totalBorrowedAmount = TokenAmount.fromAsset(
            balance.totalBorrowedAmount,
            balance.asset,
          );
          const totalBorrowableAmount = calculateTotalEffectiveBorrowableAmount({
            totalAmount: BigInt(balance.totalAmount),
            totalAvailableAmount: BigInt(balance.totalAvailableAmount),
            utilisationCapBps: balance.utilisationCapBps,
          });

          acc[balance.asset] = {
            totalAmount,
            totalAvailableAmount: TokenAmount.fromAsset(
              balance.totalAvailableAmount,
              balance.asset,
            ),
            totalBorrowedAmount,
            totalBorrowableAmount: TokenAmount.fromAsset(totalBorrowableAmount, balance.asset),
          };
          return acc;
        },
        {} as Record<ChainflipAsset, PoolBalance>,
      ),
      totalSuppliedUsd: data?.reduce((acc, pool) => acc + Number(pool.totalAmountUsd), 0),
      totalAvailableLiquidityUsd: data?.reduce(
        (acc, pool) => acc + Number(pool.totalAvailableAmountUsd),
        0,
      ),
      totalBorrowedUsd: data?.reduce((acc, pool) => acc + Number(pool.totalBorrowedAmountUsd), 0),
      stablecoinsTotalAvailableAmountUsd: lendingPools.reduce(
        (acc, pool) =>
          acc +
          (pool.asset === 'Usdc' || pool.asset === 'Usdt'
            ? Number(pool.totalAvailableAmountUsd)
            : 0),
        0,
      ),
      maxApy,
    }),
    [data, lendingPools],
  );

  const isLoading = isLiquidityProvisionApp
    ? rpcResult.isLoading || isLendingConfigLoading || isApyDataLoading || !prices
    : isCacheLoading || isLendingConfigLoading || isApyDataLoading;

  return {
    ...transformedData,
    isLoading,
  };
};
